import { styled } from '@mui/material/styles';
import { getIconsByType, iconTypes } from '@services/icons';
import { getFlexibleSize } from '@utils/cssHelpers';
import { TEXT_COLOR, getColor } from '@components/Text';
import { StyledPersonIcon } from '../MessagesList/styles';

export const Wrapper = styled('div')({
  gridArea: 'conversation',
  padding: '0px 20px',
  display: 'flex',
  flexDirection: 'column-reverse',
  overflowY: 'auto',
});

export const MessageWrapper = styled('div')(
  {
    display: 'flex',
    flexShrink: 0,
    marginTop: 4,
    position: 'relative',
    minHeight: '40px',
  },
  ({ self, isQueue }) => ({
    marginTop: isQueue ? 4 : 16,
    flexDirection: self ? 'row-reverse' : 'row',
  }),
);

export const StyledAvatar = styled('img')({
  width: 40,
  height: 40,
  objectFit: 'cover',
  borderRadius: '50%',
});

export const Message = styled('div')(
  {
    maxWidth: getFlexibleSize(565, 824),
    padding: '12px 20px',
    borderRadius: 9,
    position: 'relative',
    '&:hover .message-actions': {
      opacity: 1,
    },
  },
  ({ theme, self, isQueue }) => ({
    direction: theme.RTL ? 'rtl' : 'ltr',
    backgroundColor: self
      ? theme.palette.novotalk.$messagingOthersMessageColor
      : theme.palette.novotalk.$messagingSelfMessageColor,
    [self ? 'marginRight' : 'marginLeft']: isQueue ? 50 : 10,
    [self ? 'borderTopRightRadius' : 'borderTopLeftRadius']: isQueue ? 5 : 0,
  }),
);

export const TimeDisplay = styled('div')(
  {
    fontSize: '0.75rem',
    lineHeight: 1,
    marginTop: 4,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  ({ self }) => ({
    justifyContent: self ? 'flex-start' : 'flex-end',
  }),
);

export const DateSeparator = styled('div')(
  {
    width: '100%',
    margin: '5px 0 16px',
    display: 'flex',
    justifyContent: 'center',
    lineHeight: '15px',
    '&:before, &:after': {
      display: 'block',
      content: '""',
      height: 1,
      flexGrow: 1,
      position: 'relative',
      top: 8,
      margin: '0 10px',
    },
  },
  ({ theme }) => ({
    '&:before, &:after': {
      backgroundColor: theme.palette.novotalk.$borderColorLight,
    },
  }),
);

export const NewMessagesIndicator = styled('div')(
  {
    width: '100%',
    margin: '30px 0 10px',
    display: 'flex',
    justifyContent: 'stretch',
    lineHeight: '15px',
    '&:before, &:after': {
      display: 'block',
      content: '""',
      height: 1,
      flexGrow: 1,
      position: 'relative',
      top: 8,
    },
  },
  ({ theme }) => ({
    '&:before, &:after': {
      backgroundColor: theme.palette.novotalk.$borderColorLight,
    },
  }),
);

export const SeenIndicator = styled('div')({
  marginRight: 50,
  lineHeight: '14px',
  textAlign: 'right',
  marginTop: 4,
});

const CheckmarkIconComponent = getIconsByType(iconTypes.CHECKMARK);

export const StyledCheckmarkIcon = styled(CheckmarkIconComponent)(
  {
    fontSize: 14,
    marginRight: 2,
  },
  ({ theme }) => ({
    color: theme.palette.novotalk.$textColorDisabledLight,
  }),
);

export const MessageActions = styled('div')({
  display: 'flex',
  gap: '4px',
  alignItems: 'center',
  marginLeft: '4px',
  opacity: 0,
  transition: 'opacity 0.2s ease',
  className: 'message-actions',
});

export const ActionButton = styled('button')(
  {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '2px',
    borderRadius: '4px',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '20px',
    height: '20px',

    '& svg': {
      width: '16px',
      height: '16px',
      fill: 'currentColor',
    },

    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
      color: TEXT_COLOR.DARK_BLUE,
    },
  },
  ({ theme }) => ({
    color: getColor(theme, TEXT_COLOR.DISABLED_LIGHT),
  }),
);

export const EditInput = styled('div')(
  {
    padding: '8px 12px',
    margin: '0',
    borderRadius: '6px',
    outline: 'none',
    minHeight: 'auto',
    height: 'auto',
    fontFamily: 'inherit',
    fontSize: '14px', // Match TEXT_SIZE.H4
    lineHeight: 'inherit',
    boxSizing: 'border-box',
    whiteSpace: 'pre-line',
    overflow: 'hidden',
    display: 'inline-block',
    cursor: 'text',
    width: '100%',
    transition: 'all 0.2s ease',

    '&:focus': {
      outline: 'none',
    },
  },
  ({ theme }) => ({
    border: `1px solid ${theme.palette.novotalk.$borderColorLight}`,
    backgroundColor: theme.palette.background.paper,
    color: getColor(theme, TEXT_COLOR.DARK_BLUE),

    '&:focus': {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.background.default,
      boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
    },

    '&:hover': {
      borderColor: theme.palette.primary.light,
    },
  }),
);

export const EditButtonsContainer = styled('div')({
  display: 'flex',
  gap: '6px',
  justifyContent: 'flex-end',
  marginTop: '8px',

  '& > div': {
    width: '80px !important',
    height: '32px !important',
    fontSize: '14px !important',
    borderRadius: '16px !important',
  },
});

export const EditActionsRow = styled('div')({
  marginTop: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

export const EditEmojiPickerWrapper = styled('div')({
  display: 'flex',
  alignItems: 'center',
  marginRight: 8,
});

export const AudioMessageWrapper = styled('div')({
  minWidth: 260,
  maxWidth: 340,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
});

export const AudioMessageMeta = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 10,
});

export const AudioMessageCaption = styled('div')({
  fontSize: '12px',
  lineHeight: '16px',
  fontWeight: 600,
});

export const AudioMessageDuration = styled('div')({
  fontSize: '12px',
  lineHeight: '16px',
});
